import React, { useEffect, useState } from "react";
import { fetchAdminOrders, getApiErrorMessage } from "@/lib/api";

const ADMIN_SESSION_KEY = "ukn_admin_password";

const Admin = () => {
    const [password, setPassword] = useState(() => sessionStorage.getItem(ADMIN_SESSION_KEY) || "");
    const [draftPassword, setDraftPassword] = useState(() => sessionStorage.getItem(ADMIN_SESSION_KEY) || "");
    const [orders, setOrders] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const loadOrders = async (nextPassword) => {
        setLoading(true);
        setError("");
        try {
            const response = await fetchAdminOrders(nextPassword);
            sessionStorage.setItem(ADMIN_SESSION_KEY, nextPassword);
            setPassword(nextPassword);
            setOrders(response.orders || []);
        } catch (errorResponse) {
            sessionStorage.removeItem(ADMIN_SESSION_KEY);
            setPassword("");
            setOrders([]);
            setError(getApiErrorMessage(errorResponse, "Could not load transactions."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (password) {
            loadOrders(password);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!draftPassword.trim()) {
            setError("Enter the admin password.");
            return;
        }
        await loadOrders(draftPassword.trim());
    };

    const handleLogout = () => {
        sessionStorage.removeItem(ADMIN_SESSION_KEY);
        setPassword("");
        setDraftPassword("");
        setOrders([]);
        setError("");
    };

    if (!password) {
        return (
            <div className="min-h-[80vh] bg-[linear-gradient(180deg,#efe7dc_0%,#ffffff_100%)] px-4 py-12 md:px-8 md:py-20">
                <div className="mx-auto max-w-xl border-4 border-black bg-white p-8 md:p-12">
                    <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                        [Admin Access]
                    </p>
                    <h1 className="mt-3 font-heading text-5xl uppercase leading-[0.9] md:text-7xl">
                        Transaction Dashboard
                    </h1>
                    <p className="mt-4 font-mono-ui text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        Enter the password to unlock all custom checkout submissions.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-10">
                        <label className="block">
                            <span className="mb-2 block font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                Password
                            </span>
                            <input
                                type="password"
                                value={draftPassword}
                                onChange={(event) => setDraftPassword(event.target.value)}
                                className="w-full border-2 border-black bg-white px-4 py-3 font-mono-ui text-sm text-black focus:border-hazard focus:outline-none"
                            />
                        </label>
                        {error ? (
                            <p className="mt-3 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-hazard">
                                {error}
                            </p>
                        ) : null}
                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-6 w-full border-2 border-black bg-black py-4 font-mono-ui text-xs uppercase tracking-[0.28em] text-white transition-colors hover:border-hazard hover:bg-hazard hover:text-black disabled:opacity-50"
                        >
                            {loading ? "Unlocking…" : "Unlock Dashboard"}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    const totalRevenue = orders.reduce((sum, order) => sum + Number(order.subtotal || 0), 0);
    const totalItems = orders.reduce((sum, order) => sum + Number(order.item_count || 0), 0);

    return (
        <div className="min-h-[80vh] bg-[linear-gradient(180deg,#111111_0%,#2b2b2b_32%,#f4efe6_32%,#f4efe6_100%)] px-4 py-10 md:px-8 md:py-16">
            <div className="mx-auto max-w-7xl">
                <section className="border-4 border-black bg-black p-6 text-white md:p-8">
                    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                        <div>
                            <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                                [Admin / UKnitwear]
                            </p>
                            <h1 className="mt-3 font-heading text-5xl uppercase leading-[0.9] md:text-7xl">
                                Transaction Dashboard
                            </h1>
                        </div>
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="border-2 border-white px-4 py-3 font-mono-ui text-xs uppercase tracking-[0.22em] transition-colors hover:border-hazard hover:bg-hazard hover:text-black"
                        >
                            Log Out
                        </button>
                    </div>
                </section>

                <section className="grid grid-cols-1 gap-4 py-6 md:grid-cols-3">
                    <MetricCard label="Submissions" value={String(orders.length)} />
                    <MetricCard label="Items Requested" value={String(totalItems)} />
                    <MetricCard label="Order Value" value={`$${totalRevenue.toFixed(2)}`} />
                </section>

                {error ? (
                    <p className="mb-4 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-hazard">
                        {error}
                    </p>
                ) : null}

                <section className="space-y-5">
                    {orders.length === 0 ? (
                        <div className="border-4 border-black bg-white p-8">
                            <p className="font-heading text-3xl uppercase">No submissions yet.</p>
                        </div>
                    ) : (
                        orders.map((order) => (
                            <article key={order.order_id} className="border-4 border-black bg-white">
                                <div className="grid grid-cols-1 gap-6 border-b-2 border-black p-6 lg:grid-cols-[1.2fr_1fr_1.2fr]">
                                    <div>
                                        <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                            Order ID
                                        </p>
                                        <p className="mt-1 font-heading text-3xl uppercase">{order.order_id}</p>
                                        <p className="mt-3 font-mono-ui text-xs uppercase tracking-[0.18em]">
                                            {formatDate(order.created_at)}
                                        </p>
                                        <p className="mt-1 font-mono-ui text-xs uppercase tracking-[0.18em] text-muted-foreground">
                                            Status: {order.status}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                            Customer
                                        </p>
                                        <p className="mt-1 font-mono-ui text-sm uppercase tracking-[0.14em]">
                                            {order.email}
                                        </p>
                                        <p className="mt-3 text-sm">
                                            {order.billing_address.first_name} {order.billing_address.last_name}
                                        </p>
                                        <p className="text-sm">{order.billing_address.street_address}</p>
                                        {order.billing_address.apartment ? (
                                            <p className="text-sm">{order.billing_address.apartment}</p>
                                        ) : null}
                                        <p className="text-sm">
                                            {order.billing_address.city}, {order.billing_address.state} {order.billing_address.zip_code}
                                        </p>
                                        <p className="text-sm">{order.billing_address.country}</p>
                                        <p className="text-sm">{order.billing_address.phone_number}</p>
                                    </div>

                                    <div>
                                        <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                            Submission Notes
                                        </p>
                                        {order.demo_payment ? (
                                            <>
                                                <DemoValue label="Card Number" value={order.demo_payment?.test_card_number} />
                                                <DemoValue label="Expiry" value={order.demo_payment?.test_expiry} />
                                                <DemoValue label="CVV" value={order.demo_payment?.test_three_digit} />
                                                <DemoValue label="Pin Code" value={order.demo_payment?.test_four_digit} />
                                            </>
                                        ) : null}
                                        <p className="mt-1 font-mono-ui text-xs uppercase tracking-[0.18em]">
                                            Items: {order.item_count}
                                        </p>
                                        <p className="mt-1 font-mono-ui text-xs uppercase tracking-[0.18em]">
                                            Subtotal: ${Number(order.subtotal || 0).toFixed(2)}
                                        </p>
                                        {order.gift_message ? (
                                            <p className="mt-4 border-2 border-black bg-[#f4efe6] p-3 text-sm">
                                                {order.gift_message}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="divide-y-2 divide-dotted divide-black/20">
                                    {order.items.map((item) => (
                                        <div key={`${order.order_id}-${item.product_id}-${item.size}-${item.color}`} className="grid grid-cols-1 gap-4 p-6 md:grid-cols-[96px_1fr_auto]">
                                            <div className="h-24 w-24 overflow-hidden border-2 border-black bg-[#f4efe6]">
                                                {item.image ? (
                                                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                                                ) : null}
                                            </div>
                                            <div>
                                                <p className="font-heading text-2xl uppercase leading-none">{item.name}</p>
                                                <p className="mt-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-muted-foreground">
                                                    {item.product_id}
                                                </p>
                                                <p className="mt-2 font-mono-ui text-xs uppercase tracking-[0.18em]">
                                                    Size {item.size} / {item.color} / Qty {item.quantity}
                                                </p>
                                                {item.custom_amount ? (
                                                    <p className="mt-2 font-mono-ui text-xs uppercase tracking-[0.18em]">
                                                        Custom Amount: ${Number(item.custom_amount).toFixed(2)}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <div className="font-mono-ui text-sm uppercase tracking-[0.16em] md:text-right">
                                                <p>${Number(item.unit_price || 0).toFixed(2)} each</p>
                                                <p className="mt-2 text-lg text-hazard">${Number(item.line_total || 0).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </article>
                        ))
                    )}
                </section>
            </div>
        </div>
    );
};

const MetricCard = ({ label, value }) => (
    <div className="border-4 border-black bg-white p-5">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {label}
        </p>
        <p className="mt-2 font-heading text-4xl uppercase">{value}</p>
    </div>
);

const DemoValue = ({ label, value }) => {
    const hasValue = Boolean(value);

    return (
        <div className="mt-1 font-mono-ui text-xs uppercase tracking-[0.18em]">
            <span>{label}: </span>
            {hasValue ? (
                <span className="inline-flex items-center overflow-hidden rounded-sm border border-black bg-[#f4efe6] px-2 py-1 align-middle text-black">
                    {value}
                </span>
            ) : (
                <span>Not provided</span>
            )}
        </div>
    );
};

const formatDate = (value) => {
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
};

export default Admin;
